/* eslint-disable import/no-named-as-default */
import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import { Helmet } from "react-helmet-async";
import { useLocation, useHistory } from "react-router-dom";

import Menu from "./Menu";
import CatalogFooter from "./Footer-new";
import { GoogleOAuthProvider, googleLogout } from "@react-oauth/google";
import CookieConsent from "react-cookie-consent";
import { Typography } from "@mui/material";
import { MESSAGE_TYPES, useWebSocketStore } from "../../stores/websocketStore";
import { AuthStateContext, STEAM_SIGN_IN_URL, PLAYSAFE_SIGN_IN_URL } from "./AuthStateContext";
import UiDesignLaneGuard from "./UiDesignLaneGuard";
import PublicHeader from "../public-site/PublicHeader";
import PublicFooter from "../public-site/PublicFooter";
import TwoFactorVerify from "../auth/TwoFactorVerify";
import PlaySafeOnboardingDialog from "../auth/PlaySafeOnboardingDialog";
import AcceptMatchDialog from "./AcceptMatchDialog.jsx";
import MapVoteDialog from "./MapVoteDialog.jsx";
import MatchReadyNotification from "./MatchReadyNotification.jsx";
import useDismissExpiredMatchAccept from "../../hooks/useDismissExpiredMatchAccept";
import GlobalMatchmakingStatus from "./GlobalMatchmakingStatus.jsx";
import { isPlaySafeSignupUser } from "../lobby/lobbyAccountStatusNotice";
import {
  CANONICAL_BASE_URL,
  OG_IMAGE_URL,
  getPublicPageMeta,
  isMarketingRoute,
} from "../public-site/siteData";
import {
  REFERRAL_CODE_PATTERN,
  REFERRAL_STORAGE_KEY,
} from "../../universal/referralLinks";
import {
  clearExplicitLogout,
  hasExplicitLogout,
  markExplicitLogout,
} from "../../universal/explicitLogoutGuard";
import {
  isAbortError,
  restoreSessionWithRetry,
} from "../../universal/sessionRestore";
import { getOrCreateVisitorId } from "../../universal/track";
import { trackRedditGoogleSignup } from "../../universal/redditPixel";
import {
  armMatchPageLandedSoundForMatch,
  playMatchAcceptSound,
  playMatchFoundSoundOnce,
  clearMatchFoundSoundOnce,
  syncMatchPageLandedSoundMuteFromUser,
} from "../../universal/matchNotificationSounds";
import { toast } from "../../universal/toast";
import {
  getMatchServerReadyMatchId,
  isAlreadyOnMatchRoute,
} from "../lobby/lobbyPageUtils";
import {
  captureFromSearch,
  getStoredAttribution,
  clearStoredAttribution,
} from "../../universal/campaignAttribution";
import { TemplateTrackingProvider } from "../templates/TemplateTrackingProvider";
import { useTemplateConfig } from "../templates/useTemplateConfig";
import {
  getTemplateFooterComponent,
  getTemplateHeaderComponent,
} from "../templates/registry";
import { shouldHidePublicHeaderSpacer } from "../templates/publicHeaderLayout";
// import RTL from "./RTL";

// Signing in with PlaySafe already implies terms acceptance; this dialog is
// now a one-time, dismissible, optional-email prompt only. See
// docs/edge/terms-acceptance.md.
const PLAYSAFE_EMAIL_PROMPT_DISMISSED_KEY = "ouro_playsafe_email_prompt_dismissed";

const basePath =
  process.env.NODE_ENV === "production" ? "/edge" : "http://localhost:3010";

function PublicTemplateShell({ children, footer, headerProps, initialTemplate, spacer }) {
  const { codename, isResolved } = useTemplateConfig(initialTemplate);
  const location = useLocation();
  const isFocusedAuthStatusPage =
    location.pathname === "/auth/playsafe/status" ||
    location.pathname === "/auth/playsafe/connect-steam" ||
    location.pathname === "/auth/steam/status";
  const hidePublicHeader =
    isFocusedAuthStatusPage || (location.pathname === "/" && codename === "original");
  const hidePublicHeaderSpacer = shouldHidePublicHeaderSpacer(location.pathname, codename);
  const Header = getTemplateHeaderComponent(codename) || PublicHeader;
  const TemplateFooter = getTemplateFooterComponent(codename);

  return (
    <TemplateTrackingProvider codename={codename} isResolved={isResolved}>
      {hidePublicHeader ? null : <Header {...headerProps} />}
      {hidePublicHeader || hidePublicHeaderSpacer ? null : spacer}
      {children}
      {isFocusedAuthStatusPage ? null : TemplateFooter ? <TemplateFooter /> : footer}
    </TemplateTrackingProvider>
  );
}

PublicTemplateShell.propTypes = {
  children: PropTypes.node,
  footer: PropTypes.node,
  headerProps: PropTypes.shape({
    isAuthenticated: PropTypes.bool,
    googleResponse: PropTypes.func,
    onFailure: PropTypes.func,
  }),
  initialTemplate: PropTypes.shape({
    codename: PropTypes.string,
    isResolved: PropTypes.bool,
  }),
  spacer: PropTypes.node,
};

function clearLegacyClientAuth() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem("ouroAuthToken");
    window.localStorage.removeItem("ouroGoogleToken");
  } catch (error) {
    // Best-effort cleanup only.
  }

  if (typeof document !== "undefined") {
    document.cookie = "ouro-jwt=; Max-Age=0; path=/";
  }
}

export function createAppCleanupHandlers({
  clearLocalAuthState,
  clearLegacyClientAuth: clearLegacyClientAuthImpl = clearLegacyClientAuth,
  googleLogoutFn = googleLogout,
  logoutAction = () => {},
} = {}) {
  const applyBaseCleanup = () => {
    clearLocalAuthState?.();
    clearLegacyClientAuthImpl?.();
  };

  const applyAuthFailureState = () => {
    applyBaseCleanup();
    logoutAction?.();
  };

  const applyLoggedOutState = () => {
    applyBaseCleanup();
    try {
      googleLogoutFn?.();
    } catch (error) {
      // Best-effort cleanup only.
    }
    logoutAction?.();
  };

  return {
    applyAuthFailureState,
    applyLoggedOutState,
  };
}

export function createExplicitLogoutController(applyLoggedOutState) {
  let applied = false;

  return {
    apply() {
      if (applied) {
        return false;
      }
      applied = true;
      applyLoggedOutState?.();
      return true;
    },
    reset() {
      applied = false;
    },
  };
}

const styles = {
  spacer: {
    height: 40,
  },
  "@media (min-width: 1024px)": {
    spacer: {
      height: 82,
    },
  },
};

const SESSION_RESTORE_FETCH_OPTIONS = {
  method: "GET",
  credentials: "include",
  mode: "cors",
  cache: "no-store",
};

function App({ classes, catalog, actions, children }) {
  const location = useLocation();
  const history = useHistory();
  const [state, setState] = React.useState({
    isAuthenticated: false,
    hasStoredToken: true,
    user: null,
    showRefreshWindow: false,
    autoLoginInProgress: true,
    googleAuthFailure: null,
    twoFactorPending: false,
    twoFactorToken: null,
    sessionRestoreFailed: false,
  });
  const [playSafeEmailPromptDismissed, setPlaySafeEmailPromptDismissed] =
    React.useState(() => {
      if (typeof window === "undefined" || !window.localStorage) return false;
      try {
        return (
          window.localStorage.getItem(PLAYSAFE_EMAIL_PROMPT_DISMISSED_KEY) === "1"
        );
      } catch {
        return false;
      }
    });

  const { connect, disconnect, performLogout, subscribe, sendOwnerAction, clientId } = useWebSocketStore();
  const matchAcceptDialogStateRef = React.useRef(null);
  const matchAcceptanceCountRef = React.useRef(new Map());
  const logoutInFlightRef = React.useRef(false);
  const sessionAbortRef = React.useRef(null);
  const authGenerationRef = React.useRef(0);
  const locationPathnameRef = React.useRef(location.pathname);

  React.useEffect(() => {
    locationPathnameRef.current = location.pathname;
  }, [location.pathname]);

  React.useEffect(() => {
    syncMatchPageLandedSoundMuteFromUser(state.user || catalog?.user);
  }, [state.user, catalog?.user]);

  const clearLocalAuthState = React.useCallback(() => {
    setState((prev) => ({
      ...prev,
      isAuthenticated: false,
      hasStoredToken: false,
      user: null,
      autoLoginInProgress: false,
      googleAuthFailure: "Authentication failed",
    }));
  }, []);

  const { applyAuthFailureState, applyLoggedOutState } = React.useMemo(
    () =>
      createAppCleanupHandlers({
        clearLocalAuthState,
        clearLegacyClientAuth,
        googleLogoutFn: googleLogout,
        logoutAction: actions.logout,
      }),
    [actions.logout, clearLocalAuthState]
  );
  const explicitLogoutController = React.useMemo(
    () => createExplicitLogoutController(applyLoggedOutState),
    [applyLoggedOutState]
  );

  const onFailure = React.useCallback(() => {
    applyAuthFailureState();
  }, [applyAuthFailureState]);

  const restoreSession = React.useCallback(
    () => {
      if (hasExplicitLogout()) {
        setState((prev) => ({
          ...prev,
          isAuthenticated: false,
          hasStoredToken: false,
          user: null,
          autoLoginInProgress: false,
          sessionRestoreFailed: false,
        }));
        return;
      }

      sessionAbortRef.current?.abort();
      const controller = new AbortController();
      sessionAbortRef.current = controller;
      const generation = authGenerationRef.current;
      const isCancelled = () =>
        generation !== authGenerationRef.current || hasExplicitLogout();

      setState((prev) => ({
        ...prev,
        hasStoredToken: true,
        autoLoginInProgress: true,
        sessionRestoreFailed: false,
        googleAuthFailure: null,
      }));

      restoreSessionWithRetry({
        url: `${basePath}/auth/session`,
        fetchOptions: {
          ...SESSION_RESTORE_FETCH_OPTIONS,
          signal: controller.signal,
        },
        isCancelled,
      })
        .then((result) => {
          if (isCancelled()) {
            return;
          }

          if (result.type === "aborted") {
            return;
          }

          if (result.type === "success") {
            const user = result.user;

            // Steam OpenID is a full-page redirect flow, so unlike the Google
            // handler below there is no dedicated callback to clear attribution.
            // Clearing here ensures stale last-touch data does not linger once a
            // user is authenticated.
            clearStoredAttribution();

            actions.setUser({ user });
            setState((prev) => ({
              ...prev,
              isAuthenticated: true,
              hasStoredToken: true,
              user,
              autoLoginInProgress: false,
              sessionRestoreFailed: false,
              googleAuthFailure: null,
            }));
            return;
          }

          if (result.type === "unauthenticated") {
            onFailure();
            return;
          }

          setState((prev) => ({
            ...prev,
            isAuthenticated: false,
            hasStoredToken: true,
            autoLoginInProgress: false,
            sessionRestoreFailed: true,
          }));
        })
        .catch((error) => {
          if (isAbortError(error) || isCancelled()) {
            return;
          }
          setState((prev) => ({
            ...prev,
            isAuthenticated: false,
            hasStoredToken: true,
            autoLoginInProgress: false,
            sessionRestoreFailed: true,
          }));
        });
    },
    [actions, onFailure]
  );

  const googleResponse = ({ credential }) => {
    clearExplicitLogout();
    const acquisition = getStoredAttribution();
    const options = {
      method: "POST",
      body: JSON.stringify({
        credential,
        surface: "hero",
        visitorId: getOrCreateVisitorId(),
        ...(acquisition ? { acquisition } : {}),
      }),
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      mode: "cors",
      cache: "default",
    };

    setState((prev) => ({
      ...prev,
      hasStoredToken: true,
      autoLoginInProgress: true,
      sessionRestoreFailed: false,
      googleAuthFailure: null,
    }));

    fetch(`${basePath}/auth/google`, options)
      .then((r) => {
        return r.json().then((resp) => {
          if (resp?.twoFactorRequired) {
            setState((prev) => ({
              ...prev,
              twoFactorPending: true,
              twoFactorToken: resp.twoFactorToken,
              autoLoginInProgress: false,
              googleAuthFailure: null,
            }));
            return;
          }

          if (!r.ok || !resp?.user) {
            throw new Error(resp?.message || "Authentication failed");
          }

          const user = resp.user;

          // Clear stored attribution after sign-in; it has been sent to the
          // server and attributed to this user (or discarded for returning users).
          clearStoredAttribution();
          trackRedditGoogleSignup(resp.isNewUser === true, resp.redditConversionId);

          actions.setUser({ user });
          setState((prev) => ({
            ...prev,
            isAuthenticated: true,
            hasStoredToken: true,
            user,
            autoLoginInProgress: false,
            sessionRestoreFailed: false,
            googleAuthFailure: null,
            twoFactorPending: false,
            twoFactorToken: null,
          }));
        });
      })
      .catch(onFailure);
  };

  React.useEffect(() => {
    clearLegacyClientAuth();
    restoreSession();
    // Mount-only: `actions` is rebound when route children change, and a
    // second restore during logout races the still-valid session cookie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const refCode = params.get("ref");
      if (refCode && REFERRAL_CODE_PATTERN.test(refCode)) {
        window.localStorage.setItem(REFERRAL_STORAGE_KEY, refCode.toUpperCase());
      }
    } catch (_) {
      // Best-effort referral capture only.
    }
  }, [location.search]);

  // Last-touch campaign attribution: overwrite on every valid utm_source + utm_id visit.
  React.useEffect(() => {
    try {
      captureFromSearch(location.search);
    } catch (_) {
      // Best-effort attribution capture only.
    }
  }, [location.search]);

  React.useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const twoFactorToken = params.get("twoFactorToken");
      if (!twoFactorToken) {
        return;
      }

      setState((prev) => ({
        ...prev,
        twoFactorPending: true,
        twoFactorToken,
        autoLoginInProgress: false,
        googleAuthFailure: null,
      }));

      params.delete("twoFactorToken");
      history.replace({
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
        hash: location.hash,
      });
    } catch (_) {
      // Best-effort deep-link handling only.
    }
  }, [history, location.hash, location.pathname, location.search]);

  // Connect WebSocket when user is authenticated
  React.useEffect(() => {
    if (state.isAuthenticated && state.user) {
      explicitLogoutController.reset();
      connect(state.user);
    } else {
      disconnect();
    }
  }, [state.isAuthenticated, state.user, connect, disconnect, explicitLogoutController]);

  React.useEffect(() => {
    const unsubscribe = subscribe(({ message }) => {
      if (!message?.messageId) return;

      if (message.messageId === MESSAGE_TYPES.SESSION_ENDED) {
        if (!logoutInFlightRef.current) {
          markExplicitLogout();
          explicitLogoutController.apply();
        }
        return;
      }

      switch (message.messageId) {
        case MESSAGE_TYPES.MATCH_FOUND_NOTIFICATION:
          playMatchFoundSoundOnce(message.matchId);
          actions.showMatchFoundNotification(
            message.matchId,
            message.timeoutSeconds,
            message.map,
            message.totalPlayers,
            {
              expiresAt: message.expiresAt,
              hasAccepted: message.hasAccepted,
              acceptedCount: message.acceptedCount,
              allAccepted: message.allAccepted,
              slots: message.slots,
              partyAcceptedUserIds: message.partyAcceptedUserIds,
              location: message.location || null,
              mapVotePending: message.mapVotePending,
              teams: message.teams,
            }
          );
          actions.clearQueueStatus();
          break;

        case MESSAGE_TYPES.MATCH_ACCEPTANCE_UPDATE: {
          try {
            const matchId = message.matchId;
            const nextAcceptedCount = Number(message.acceptedCount ?? 0);
            const prevAcceptedCount =
              matchAcceptanceCountRef.current.get(matchId) ?? 0;

            matchAcceptanceCountRef.current.set(matchId, nextAcceptedCount);

            const activeMatchId = matchAcceptDialogStateRef.current?.matchId;
            if (
              activeMatchId &&
              activeMatchId === matchId &&
              nextAcceptedCount > prevAcceptedCount
            ) {
              playMatchAcceptSound();
            }
          } catch (error) {
            // Never let sound issues break the handler.
          }

          actions.updateMatchAcceptanceStatus({
            matchId: message.matchId,
            acceptedCount: message.acceptedCount,
            totalPlayers: message.totalPlayers,
            allAccepted: message.allAccepted,
            hasAccepted: message.hasAccepted,
            expiresAt: message.expiresAt,
            slots: message.slots,
            partyAcceptedUserIds: message.partyAcceptedUserIds,
            mapVotePending: message.mapVotePending,
            map: message.map,
            teams: message.teams,
          });
          break;
        }

        case MESSAGE_TYPES.MATCH_ACCEPTANCE_CANCELLED: {
          clearMatchFoundSoundOnce(message.matchId);
          const cancelledMatchId =
            message.matchId != null && message.matchId !== ""
              ? String(message.matchId)
              : null;
          const activeMatchId =
            matchAcceptDialogStateRef.current?.matchId != null
              ? String(matchAcceptDialogStateRef.current.matchId)
              : null;
          const wasLiveAcceptPrompt =
            Boolean(cancelledMatchId) &&
            Boolean(activeMatchId) &&
            cancelledMatchId === activeMatchId;

          actions.matchAcceptanceCancelled({
            matchId: message.matchId,
            outcome: message.outcome,
            reason: message.reason,
            playerQueueActionStartTs: message.playerQueueActionStartTs,
          });

          if (wasLiveAcceptPrompt && message.outcome === "requeued") {
            toast.warning(
              "A player didn't accept — searching again",
              { durationMs: 5000 },
            );
          }
          break;
        }

        case MESSAGE_TYPES.MAP_VOTE_STARTED:
          actions.startMapVote({
            matchId: message.matchId,
            candidates: message.candidates,
            blockedMaps: message.blockedMaps,
            expiresAt: message.expiresAt,
            durationSeconds: message.durationSeconds,
            voters: message.voters,
            votes: message.votes,
            myVote: message.myVote,
            teamStats: message.teamStats,
          });
          break;

        case MESSAGE_TYPES.MAP_VOTE_UPDATE:
          actions.updateMapVote({
            matchId: message.matchId,
            candidates: message.candidates,
            blockedMaps: message.blockedMaps,
            expiresAt: message.expiresAt,
            durationSeconds: message.durationSeconds,
            voters: message.voters,
            votes: message.votes,
            myVote: message.myVote,
            teamStats: message.teamStats,
          });
          break;

        case MESSAGE_TYPES.MAP_VOTE_RESOLVED:
          actions.resolveMapVote({
            matchId: message.matchId,
            map: message.map,
            tally: message.tally,
            votes: message.votes,
            voters: message.voters,
            teamStats: message.teamStats,
            wasTie: message.wasTie,
            tieBrokenBy: message.tieBrokenBy,
            tiedMaps: message.tiedMaps,
            resolvedBy: message.resolvedBy,
            topCount: message.topCount,
          });
          break;

        case MESSAGE_TYPES.MATCHMAKING_STARTED_NOTIFICATION:
          actions.notifyMatchmakingStarted({
            playerQueueActionStartTs: message.playerQueueActionStartTs,
          });
          break;

        case MESSAGE_TYPES.MATCHMAKING_STOPPED_NOTIFICATION:
          actions.resetMatchmakingStarted();
          actions.clearQueueStatus();
          break;

        case MESSAGE_TYPES.QUEUE_STATUS_UPDATE:
          actions.updateQueueStatus({
            playersInQueue: message.playersInQueue,
            playersNeeded: message.playersNeeded,
            algorithmEnabled: message.algorithmEnabled,
            hint: message.hint,
            estimatedWaitSeconds: message.estimatedWaitSeconds,
            smallerMatchOffer: message.smallerMatchOffer,
          });
          if (
            catalog?.currentLobbyCode &&
            Array.isArray(catalog?.currentLobby?.members) &&
            catalog.currentLobby.members.length > 1 &&
            catalog.lobbyState !== "searching" &&
            !catalog.matchmakingStarted
          ) {
            actions.notifyMatchmakingStarted();
          }
          break;

        case MESSAGE_TYPES.MATCH_SERVER_READY: {
          const readyMatchId = getMatchServerReadyMatchId(message);
          clearMatchFoundSoundOnce(readyMatchId);
          actions.lobbyStopSearch({ userId: state.user?.userId });
          actions.closeMatchAcceptDialog();
          actions.closeMapVote();

          // Handshake/reconnect replays this event. Clearing match data while
          // already on this match page stops polling and leaves the overlay up.
          if (isAlreadyOnMatchRoute(locationPathnameRef.current, readyMatchId)) {
            break;
          }

          actions.clearMatchData();

          if (readyMatchId) {
            armMatchPageLandedSoundForMatch(readyMatchId);
            history.push(message.redirectTo || `/match/${readyMatchId}`);
          } else if (message.redirectTo) {
            history.push(message.redirectTo);
          }
          break;
        }

        default:
          break;
      }
    });

    return unsubscribe;
  }, [actions, catalog, explicitLogoutController, history, state.user?.userId, subscribe]);

  const logout = React.useCallback(() => {
    markExplicitLogout();
    logoutInFlightRef.current = true;
    authGenerationRef.current += 1;
    sessionAbortRef.current?.abort();
    void performLogout({
      httpLogout: async () => {
        try {
          await fetch(`${basePath}/auth/logout`, {
            method: "POST",
            credentials: "include",
            mode: "cors",
            cache: "no-cache",
          });
        } catch (error) {
          // Best-effort logout request only.
        }
      },
      clearLocalSession: async () => {
        logoutInFlightRef.current = false;
        explicitLogoutController.reset();
        explicitLogoutController.apply();
        history.push("/");
      },
    });
  }, [explicitLogoutController, performLogout, history]);

  const completeTwoFactor = React.useCallback(
    (user) => {
      clearExplicitLogout();
      actions.setUser({ user });
      setState((prev) => ({
        ...prev,
        isAuthenticated: true,
        hasStoredToken: true,
        user,
        autoLoginInProgress: false,
        twoFactorPending: false,
        twoFactorToken: null,
        googleAuthFailure: null,
        sessionRestoreFailed: false,
      }));
    },
    [actions]
  );

  const cancelTwoFactor = React.useCallback(() => {
    setState((prev) => ({
      ...prev,
      twoFactorPending: false,
      twoFactorToken: null,
      hasStoredToken: false,
      autoLoginInProgress: false,
    }));
  }, []);

  const dismissPlaySafeEmailPrompt = React.useCallback(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        window.localStorage.setItem(PLAYSAFE_EMAIL_PROMPT_DISMISSED_KEY, "1");
      } catch {
        // localStorage may be unavailable
      }
    }
    setPlaySafeEmailPromptDismissed(true);
  }, []);

  const handlePlaySafeOnboardingComplete = React.useCallback(async () => {
    const refreshedUser = await actions.refreshSessionUser();
    setState((prev) => ({
      ...prev,
      user: refreshedUser || prev.user,
    }));
    dismissPlaySafeEmailPrompt();
  }, [actions, dismissPlaySafeEmailPrompt]);

  const handlePlaySafeOnboardingDismiss = React.useCallback(() => {
    dismissPlaySafeEmailPrompt();
  }, [dismissPlaySafeEmailPrompt]);

  const {
    isAuthenticated,
    hasStoredToken,
    user,
    autoLoginInProgress,
    twoFactorPending,
    sessionRestoreFailed,
  } = state;
  const {
    performLogout: shouldPerformLogout,
    matchAcceptDialogState = false,
    mapVoteState = false,
    lobbyState = "standby",
    matchmakingStarted = false,
    queueStatus = null,
  } = catalog;

  const onAcceptMatch = React.useCallback(() => {
    if (!matchAcceptDialogState?.matchId) return;
    actions.lobbyAcceptMatch({ matchId: matchAcceptDialogState.matchId });
    sendOwnerAction({
      messageId: "accept-match",
      matchId: matchAcceptDialogState.matchId,
      userId: user?.userId,
      clientId,
    });
  }, [actions, clientId, matchAcceptDialogState, sendOwnerAction, user?.userId]);

  const onVoteMap = React.useCallback(
    (map) => {
      const matchId = mapVoteState?.matchId;
      if (!matchId || !map) return;
      // A non-owner tab's send is dropped while ownership is requested, so only
      // paint the optimistic pick once the vote is really out — otherwise the
      // tile would claim a vote the server never saw.
      const sent = sendOwnerAction({
        messageId: "cast-map-vote",
        matchId,
        map,
        userId: user?.userId,
        clientId,
      });
      if (sent) {
        actions.castMapVoteLocally({ matchId, map });
      }
    },
    [actions, clientId, mapVoteState?.matchId, sendOwnerAction, user?.userId]
  );

  const onDismissMapVote = React.useCallback(() => {
    actions.closeMapVote();
  }, [actions]);

  React.useEffect(() => {
    matchAcceptDialogStateRef.current = matchAcceptDialogState;
  }, [matchAcceptDialogState]);

  const onAcceptDeadlineExpired = React.useCallback(
    ({ matchId }) => {
      actions.matchAcceptanceCancelled({
        matchId,
        outcome: "returned-to-lobby",
        reason: "accept-timeout",
      });
    },
    [actions]
  );
  useDismissExpiredMatchAccept({
    matchAcceptDialogState,
    onExpired: onAcceptDeadlineExpired,
  });

  const baseDescription =
    "Ouro is a trust-focused Counter-Strike matchmaking platform built around verified players and better match quality.";
  const pathname = location?.pathname || "/";
  const isActiveLobbyPath = pathname === "/lobby";
  const queueStartedAt =
    catalog?.user?.playerQueueActionStartTs ||
    user?.playerQueueActionStartTs ||
    null;
  const showGlobalSearchingStatus =
    !isActiveLobbyPath &&
    !matchAcceptDialogState?.matchId &&
    (lobbyState === "searching" || Boolean(matchmakingStarted));
  const isPublicShellRoute = isMarketingRoute(pathname);
  const publicPageMeta = getPublicPageMeta(pathname);
  // Signing in already implies terms acceptance for every provider (see
  // docs/edge/terms-acceptance.md), so this no longer gates on
  // `termsAccepted` — it's just a one-time, skippable nudge to add a
  // recovery email for PlaySafe sign-ins that don't have one yet.
  const showPlaySafeOnboarding =
    !twoFactorPending &&
    isAuthenticated &&
    Boolean(user) &&
    isPlaySafeSignupUser(user) &&
    user.emailIsSynthetic === true &&
    !playSafeEmailPromptDismissed;

  // Mandatory gate: PlaySafe identities are never treated as verified until a
  // steamId64 is confirmed. Redirect away from anything but the Connect
  // Steam / status screens whenever the session lands in this state (e.g. a
  // stale tab, a direct lobby link, or a refresh mid-flow).
  React.useEffect(() => {
    if (twoFactorPending || !isAuthenticated || !user) return;
    if (user.accountStatus !== "pending_steam_link") return;
    if (
      pathname === "/auth/playsafe/connect-steam" ||
      pathname === "/auth/playsafe/status"
    ) {
      return;
    }
    history.replace("/auth/playsafe/connect-steam");
  }, [twoFactorPending, isAuthenticated, user, pathname, history]);

  const authContextValue = React.useMemo(
    () => ({
      isAuthenticated,
      hasStoredToken,
      autoLoginInProgress,
      twoFactorPending,
      performLogout: shouldPerformLogout,
      sessionRestoreFailed,
      retrySessionRestore: restoreSession,
      googleResponse,
      onFailure,
      steamSignInUrl: STEAM_SIGN_IN_URL,
      playsafeSignInUrl: PLAYSAFE_SIGN_IN_URL,
      user,
      logout,
    }),
    [
      isAuthenticated,
      hasStoredToken,
      autoLoginInProgress,
      twoFactorPending,
      shouldPerformLogout,
      sessionRestoreFailed,
      restoreSession,
      googleResponse,
      onFailure,
      user,
      logout,
    ]
  );

  return (
    // <RTL>
    <div>
      <GoogleOAuthProvider clientId="1068129342176-704fo06p1eqtqp9me1o948btj290qsf5.apps.googleusercontent.com">
        {twoFactorPending ? (
          <TwoFactorVerify
            twoFactorToken={state.twoFactorToken}
            onSuccess={completeTwoFactor}
            onCancel={cancelTwoFactor}
          />
        ) : (
          !isPublicShellRoute ? (
            <Menu
              actions={actions}
              catalog={catalog}
              logout={logout}
              autoLoginInProgress={autoLoginInProgress}
              isAuthenticated={isAuthenticated}
              googleResponse={googleResponse}
              user={user}
              onFailure={onFailure}
            />
          ) : null
        )}
        <Helmet>
          <html lang="en" />
          <meta charSet="utf-8" />
          <title>{publicPageMeta.title}</title>
          <meta
            name="description"
            content={publicPageMeta.description || baseDescription}
          />
          <link
            rel="canonical"
            href={`${CANONICAL_BASE_URL}${pathname === "/" ? "" : pathname}`}
          />

          {/* Open Graph */}
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Ouro" />
          <meta property="og:title" content={publicPageMeta.title} />
          <meta
            property="og:description"
            content={publicPageMeta.description || baseDescription}
          />
          <meta
            property="og:url"
            content={`${CANONICAL_BASE_URL}${pathname === "/" ? "" : pathname}`}
          />
          <meta property="og:image" content={OG_IMAGE_URL} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content="Ouro — CS2 Matchmaking" />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={publicPageMeta.title} />
          <meta
            name="twitter:description"
            content={publicPageMeta.description || baseDescription}
          />
          <meta name="twitter:image" content={OG_IMAGE_URL} />
        </Helmet>

        {isPublicShellRoute ? null : <div className={classes.spacer}></div>}

        <AuthStateContext.Provider value={authContextValue}>
          <UiDesignLaneGuard actions={actions} />
          {isPublicShellRoute ? (
            <PublicTemplateShell
              headerProps={{
                isAuthenticated,
                googleResponse,
                onFailure,
              }}
              footer={<PublicFooter />}
              initialTemplate={catalog?.publicTemplate}
              spacer={<div className={classes.spacer}></div>}
            >
              {children}
            </PublicTemplateShell>
          ) : (
            children
          )}
        </AuthStateContext.Provider>
        <PlaySafeOnboardingDialog
          open={showPlaySafeOnboarding}
          onComplete={handlePlaySafeOnboardingComplete}
          onDismiss={handlePlaySafeOnboardingDismiss}
        />
        {showGlobalSearchingStatus ? (
          <GlobalMatchmakingStatus
            timestamp={queueStartedAt}
            queueStatus={queueStatus}
            user={catalog?.user || user}
            actions={actions}
          />
        ) : null}
        {matchAcceptDialogState?.matchId && !isActiveLobbyPath ? (
          isPublicShellRoute ? (
            <AcceptMatchDialog
              matchId={matchAcceptDialogState.matchId}
              onAcceptMatch={onAcceptMatch}
              timeoutSeconds={matchAcceptDialogState.timeoutSeconds}
              matchAcceptDialogState={matchAcceptDialogState}
            />
          ) : (
            <MatchReadyNotification
              matchAcceptance={matchAcceptDialogState}
              onAcceptMatch={onAcceptMatch}
            />
          )
        ) : null}
        {/* Unlike the accept prompt, the vote has no lobby-embedded
            equivalent, so it renders on every route. */}
        {mapVoteState?.matchId ? (
          <MapVoteDialog
            matchId={mapVoteState.matchId}
            candidates={mapVoteState.candidates}
            blockedMaps={mapVoteState.blockedMaps}
            expiresAt={mapVoteState.expiresAt}
            durationSeconds={mapVoteState.durationSeconds}
            voters={mapVoteState.voters}
            votes={mapVoteState.votes}
            myVote={mapVoteState.myVote}
            resolvedMap={mapVoteState.resolvedMap}
            wasTie={mapVoteState.wasTie}
            tieBrokenBy={mapVoteState.tieBrokenBy}
            topCount={mapVoteState.topCount}
            teamStats={mapVoteState.teamStats}
            currentUserId={user?.userId}
            onVote={onVoteMap}
            onDismiss={onDismissMapVote}
          />
        ) : null}
        {isPublicShellRoute ? null : <CatalogFooter />}
        <CookieConsent
          location="bottom"
          buttonText={"ACCEPT"}
          cookieName="ouro-matchmaking-consent"
          style={{ background: "#2B373B" }}
          buttonStyle={{
            color: "#fefefe",
            padding: 8,
            position: "relative",
            top: 10,
            right: 5,
            fontWeight: 525,
            background: "#E8A33D",
            fontSize: "11pt",
            borderRadius: 5,
          }}
        >
          <Typography>
            {
              "We use cookies to enhance your browsing experience and analyze site traffic."
            }{" "}
            <br />
            {
              'By clicking "Accept" you consent to the use of cookies in accordance with our Cookie Policy. For more information, please read our Privacy Policy.'
            }
          </Typography>
        </CookieConsent>
      </GoogleOAuthProvider>
    </div>
    // </RTL>
  );
}

App.propTypes = {
  children: PropTypes.node,
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
};

export default withStyles(styles)(App);
